import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as dateFns from 'date-fns';

import { getAccountFullName } from '@main-client/src/app/user/user.helpers';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

const IN_PERSON_MEETING_METHOD = 'in-person';
const VALID_URL_REGEX =
  /^[A-Za-z][A-Za-z\d.+-]*:\/*(?:\w+(?::\w+)?@)?[^\s/]+(?::\d+)?(?:\/[\w#!:.?+=&%@\-/]*)?$/;

@Injectable({ providedIn: 'root' })
export class FormValidatorService {
  validDate(form: AbstractControl): any {
    if (
      !form ||
      !form.get('date') ||
      !form.get('start') ||
      !form.get('end') ||
      !form.get('date').value ||
      !form.get('start').value ||
      !form.get('end').value
    ) {
      return null;
    }
    const date = new Date(form.get('date').value);
    const startTime = new Date(form.get('start').value);

    const startHour = dateFns.getHours(startTime);
    const startMinute = dateFns.getMinutes(startTime);
    let sanitizedStartTime = new Date(date.valueOf());
    sanitizedStartTime = dateFns.setHours(sanitizedStartTime, startHour);
    sanitizedStartTime = dateFns.setMinutes(sanitizedStartTime, startMinute);
    const endTime = new Date(form.get('end').value);
    const endHour = dateFns.getHours(endTime);
    const endMinute = dateFns.getMinutes(endTime);
    let sanitizedEndTime = new Date(date.valueOf());
    sanitizedEndTime = dateFns.setHours(sanitizedEndTime, endHour);
    sanitizedEndTime = dateFns.setMinutes(sanitizedEndTime, endMinute);
    const now = new Date();
    const dateHasOccurred = dateFns.isAfter(now, sanitizedStartTime);
    const startTimeOccurred = dateFns.isAfter(now, sanitizedStartTime);
    const endTimeOccurred = dateFns.isAfter(now, sanitizedEndTime);
    const startAfterEnd = dateFns.isAfter(sanitizedStartTime, sanitizedEndTime);
    if (dateHasOccurred) {
      return { dateBeforeNow: true };
    }
    if (startTimeOccurred) {
      return { startBeforeNow: true };
    }
    if (endTimeOccurred) {
      return { endBeforeNow: true };
    }
    if (startAfterEnd) {
      return { startAfterEnd: true };
    }
    if (form.get('date').hasError('matDatepickerMin')) {
      return { minDate: form.get('date').errors.matDatepickerMin.min };
    }
    return null;
  }

  validLocation(form: AbstractControl): any {
    if (!form) {
      return;
    }
    const meetingMethod = get(form, 'value.meeting_method', '');
    const isInPerson = isEqual(IN_PERSON_MEETING_METHOD, meetingMethod);
    if (!isInPerson) {
      return null;
    }
    const hasCoordinates = isEqual(
      2,
      get(form, 'value.event_location.location.coordinates.length', 0),
    );
    const selectedLocationAddress = get(
      form,
      'value.event_location.formatted_address',
      get(form, 'value.event_location.address', ''),
    );
    const validFormattedAddress = isEqual(
      get(form, 'value.locationInput'),
      selectedLocationAddress,
    );
    if (!form.value.locationInput) {
      return null;
    }
    if (!selectedLocationAddress) {
      return { noLocation: true };
    }
    if (validFormattedAddress && hasCoordinates) {
      return null;
    }
    return { invalidLocation: true };
  }

  validSchedulerLocation(form: AbstractControl) {
    const fallbackLocation = `${get(form, 'value.event_location.name')} (${get(
      form,
      'value.event_location.address',
    )})`;
    const formattedLocationFromAutocomplete = get(
      form,
      'value.event_location.formatted_address',
      fallbackLocation,
    );
    const locationInput = form.value.locationInput;
    const isValid =
      formattedLocationFromAutocomplete &&
      isEqual(formattedLocationFromAutocomplete, locationInput);
    return isValid ? null : { invalidLocation: true };
  }

  validFeaturedAccount(form: AbstractControl): ValidationErrors | null {
    if (!form) {
      return;
    }
    const featuredAccount = form.value.first_feature_account;
    const featuredAccountInput = form?.value?.featuredAccountInput || '';
    const FeaturedAccountValid =
      featuredAccountInput === featuredAccount?.email ||
      featuredAccountInput === getAccountFullName(featuredAccount);
    if (!featuredAccount) {
      return { invalidAccount: true };
    }
    if (featuredAccount && FeaturedAccountValid) {
      return null;
    }
    return { invalidAccount: true };
  }

  validEditorAccounts(form: AbstractControl): ValidationErrors | null {
    if (!form) {
      return;
    }
    const editorAccounts = form.value.editor_accounts;
    const editorAccountsValid = editorAccounts.every(
      (account: any) =>
        account.email === get(form, 'value.editorAccountsInput', ''),
    );
    if (editorAccountsValid || 0 === form.value.editorAccountsInput.length) {
      return null;
    }
    return { invalidAccount: true };
  }

  validDateRange(form: AbstractControl): any {
    if (!form || !form.get('startDate') || !form.get('endDate').value) {
      return null;
    }
    const endDate = form.get('endDate').value;
    const startDate = form.get('startDate').value;
    const startAfterEnd = dateFns.isAfter(
      new Date(startDate),
      new Date(endDate),
    );
    if (startAfterEnd) {
      return { startAfterEnd: true };
    }
    if (form.get('startDate').hasError('matDatepickerMin')) {
      return {
        minStartDate: form.get('startDate').errors.matDatepickerMin.min,
      };
    }
    if (form.get('endDate').hasError('matDatepickerMin')) {
      return { minEndDate: form.get('endDate').errors.matDatepickerMin.min };
    }
    return null;
  }

  validUrl(form: AbstractControl) {
    return !form.value || VALID_URL_REGEX.test(form.value)
      ? null
      : { invalidUrl: true };
  }

  minArrayElementsLength(min: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null =>
      min <= control?.value?.length ? null : { minArrayElementsLength: true };
  }

  validMaxLines(limit: number): ValidatorFn {
    return (form: AbstractControl): any => {
      const items = form.value
        .split('\n')
        .filter((item: string) => !isEmpty(item));
      return items.length >= limit ? { maxLines: true } : null;
    };
  }

  maxArrayElementsLength(max: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null =>
      max < control.value.length ? null : { maxArrayElementsLength: true };
  }
}
